/**
 * Restore the WooCommerce / FacetWP product archive after returning from a
 * single product.
 *
 * This follows FacetWP's recommended idea: save the number of real Load More
 * clicks, then replay those clicks on browser Back / refresh instead of
 * manually replacing product markup.
 */
(function () {
    'use strict';

    var storagePrefix = 'thermotile:fwp-archive-state:v7:';
    var legacyStorageKeys = [
        'thermotile:product-archive-return:v6',
        'thermotile:product-archive-return:v5',
        'thermotile:product-archive-return:v4',
        'thermotile:product-archive-return:v3',
        'thermotile:product-archive-return:v2',
        'thermotile:product-archive-return:v1'
    ];
    var legacyStoragePrefixes = [
        'fwp_archive_state:'
    ];
    var maximumAge = 6 * 60 * 60 * 1000;
    var maxRestoreAttempts = 50;

    var restoring = false;
    var waitingForFacetwp = false;
    var pendingLoadMoreClick = false;
    var pageShown = false;
    var productNavigationStarted = false;
    var archiveNavigationStarted = false;
    var restoreTarget = null;
    var restoreTimer = 0;
    var restoreFailSafeTimer = 0;

    var productSelector = '.products__item, .products-item, .product';
    var productLinkSelector = [
        '.facetwp-template .products__item a[href]',
        '.facetwp-template .products-item a[href]',
        '.facetwp-template .product a[href]',
        '.products__grid.facetwp-template a[href]'
    ].join(', ');

    var getScrollY = function () {
        return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    };

    var toPositiveInteger = function (value, fallback) {
        var number = parseInt(value, 10);

        return Number.isFinite(number) && number > 0 ? number : fallback;
    };

    var getArchiveUrl = function () {
        return window.location.pathname + window.location.search;
    };

    var getStorageKey = function (url) {
        return storagePrefix + (url || getArchiveUrl());
    };

    var getDefaultState = function () {
        return {
            archiveUrl: getArchiveUrl(),
            isReturning: false,
            lastProductHref: '',
            loadMoreClicks: 0,
            productId: 0,
            productTop: 0,
            restoreOnReload: false,
            scrollY: 0,
            updatedAt: Date.now(),
            version: 7
        };
    };

    var isFreshState = function (state) {
        return !!(
            state
            && 7 === state.version
            && state.archiveUrl === getArchiveUrl()
            && state.updatedAt
            && Date.now() - state.updatedAt <= maximumAge
        );
    };

    var readState = function () {
        try {
            var value = window.sessionStorage.getItem(getStorageKey());
            var state = value ? JSON.parse(value) : null;

            return isFreshState(state) ? state : getDefaultState();
        } catch (error) {
            return getDefaultState();
        }
    };

    var writeState = function (state) {
        if (!state) {
            return;
        }

        try {
            state.archiveUrl = getArchiveUrl();
            state.updatedAt = Date.now();
            state.version = 7;
            window.sessionStorage.setItem(getStorageKey(), JSON.stringify(state));
        } catch (error) {
            // sessionStorage can be unavailable in private/restricted browsing.
        }
    };

    var removeCurrentState = function () {
        try {
            window.sessionStorage.removeItem(getStorageKey());
        } catch (error) {
            // Ignore blocked storage.
        }
    };

    var removeMatchingStorageKeys = function (matcher) {
        try {
            var keys = [];

            for (var index = 0; index < window.sessionStorage.length; index += 1) {
                var key = window.sessionStorage.key(index);

                if (key && matcher(key)) {
                    keys.push(key);
                }
            }

            keys.forEach(function (key) {
                window.sessionStorage.removeItem(key);
            });
        } catch (error) {
            // Ignore blocked storage.
        }
    };

    var removeLegacyState = function () {
        try {
            legacyStorageKeys.forEach(function (key) {
                window.sessionStorage.removeItem(key);
            });
        } catch (error) {
            // Ignore blocked storage.
        }

        removeMatchingStorageKeys(function (key) {
            return legacyStoragePrefixes.some(function (prefix) {
                return 0 === key.indexOf(prefix);
            });
        });
    };

    var removeAllArchiveStates = function () {
        removeMatchingStorageKeys(function (key) {
            return 0 === key.indexOf(storagePrefix)
                || 0 === key.indexOf('thermotile:product-archive-return:')
                || legacyStoragePrefixes.some(function (prefix) {
                    return 0 === key.indexOf(prefix);
                });
        });
    };

    var getNavigationType = function () {
        if (window.performance && 'function' === typeof window.performance.getEntriesByType) {
            var entries = window.performance.getEntriesByType('navigation');

            if (entries.length && entries[0].type) {
                return entries[0].type;
            }
        }

        if (window.performance && window.performance.navigation) {
            if (1 === window.performance.navigation.type) {
                return 'reload';
            }

            if (2 === window.performance.navigation.type) {
                return 'back_forward';
            }
        }

        return 'navigate';
    };

    var isReloadNavigation = function () {
        return 'reload' === getNavigationType();
    };

    var isSameTabNavigationLink = function (event, link) {
        if (!link || !link.href || link.hasAttribute('download')) {
            return false;
        }

        if (
            event
            && (
                event.defaultPrevented
                || (undefined !== event.button && 0 !== event.button)
                || event.metaKey
                || event.ctrlKey
                || event.shiftKey
                || event.altKey
            )
        ) {
            return false;
        }

        if ('_blank' === link.target || link.matches('.add_to_cart_button, .ajax_add_to_cart')) {
            return false;
        }

        try {
            var destination = new URL(link.href, window.location.href);
            var currentPath = window.location.pathname + window.location.search;
            var destinationPath = destination.pathname + destination.search;

            return destination.origin !== window.location.origin || destinationPath !== currentPath;
        } catch (error) {
            return false;
        }
    };

    var isProductArchiveLink = function (event, link) {
        if (!isSameTabNavigationLink(event, link) || !link.matches(productLinkSelector)) {
            return false;
        }

        if (!link.closest(productSelector)) {
            return false;
        }

        try {
            var destination = new URL(link.href, window.location.href);

            return destination.origin === window.location.origin
                && destination.pathname !== window.location.pathname;
        } catch (error) {
            return false;
        }
    };

    var getProductId = function (card) {
        var match = card && String(card.className).match(/(?:^|\s)post-(\d+)(?:\s|$)/);

        return match ? parseInt(match[1], 10) : 0;
    };

    var rememberProductReturn = function (link) {
        var card = link.closest(productSelector);
        var state = readState();

        state.isReturning = true;
        state.lastProductHref = link.href;
        state.productId = getProductId(card);
        state.productTop = card ? card.getBoundingClientRect().top : 0;
        state.restoreOnReload = false;
        state.scrollY = getScrollY();

        writeState(state);
    };

    var rememberReloadRestore = function () {
        var state = readState();

        if (toPositiveInteger(state.loadMoreClicks, 0) < 1) {
            return;
        }

        state.isReturning = false;
        state.restoreOnReload = true;
        state.scrollY = getScrollY();

        writeState(state);
    };

    var rememberLoadMoreClick = function () {
        var state = readState();

        state.loadMoreClicks = toPositiveInteger(state.loadMoreClicks, 0) + 1;
        state.productId = 0;
        state.productTop = 0;
        state.restoreOnReload = false;
        state.scrollY = getScrollY();

        pendingLoadMoreClick = true;
        writeState(state);
    };

    var findLoadMoreButton = function () {
        var buttons = document.querySelectorAll('.facetwp-load-more:not(.facetwp-hidden)');

        for (var index = 0; index < buttons.length; index += 1) {
            var button = buttons[index];

            if (
                !button.disabled
                && !button.classList.contains('loading')
                && null !== button.offsetParent
            ) {
                return button;
            }
        }

        return null;
    };

    var scrollToRestoredPosition = function (target) {
        window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
                var top = toPositiveInteger(target.scrollY, 0);

                if (target.productId) {
                    var card = document.querySelector('.post-' + target.productId);

                    if (card) {
                        top = getScrollY()
                            + card.getBoundingClientRect().top
                            - Number(target.productTop || 0);
                    }
                }

                window.scrollTo(0, Math.max(0, top));
            });
        });
    };

    var finishRestoring = function () {
        var target = restoreTarget || {
            productId: 0,
            productTop: 0,
            scrollY: 0
        };

        restoring = false;
        waitingForFacetwp = false;
        restoreTarget = null;
        pendingLoadMoreClick = false;

        if (restoreTimer) {
            window.clearTimeout(restoreTimer);
            restoreTimer = 0;
        }

        if (restoreFailSafeTimer) {
            window.clearTimeout(restoreFailSafeTimer);
            restoreFailSafeTimer = 0;
        }

        scrollToRestoredPosition(target);

        if ('function' === typeof window.thermotileRefreshAOS) {
            window.thermotileRefreshAOS(true);
        }

        if (window.Thermotile && 'function' === typeof window.Thermotile.refreshScrollTopButton) {
            window.Thermotile.refreshScrollTopButton();
        }
    };

    var queueRestoreAttempt = function (delay) {
        if (!restoring || waitingForFacetwp || restoreTimer) {
            return;
        }

        restoreTimer = window.setTimeout(function () {
            restoreTimer = 0;
            restoreNextBatch();
        }, delay || 80);
    };

    var waitForFacetwp = function () {
        if (!window.FWP || !FWP.loaded) {
            restoreTarget.attempts += 1;

            if (restoreTarget.attempts > maxRestoreAttempts) {
                finishRestoring();
                return true;
            }

            queueRestoreAttempt(120);
            return true;
        }

        return false;
    };

    var restoreNextBatch = function () {
        if (!restoring || !restoreTarget) {
            return;
        }

        if (restoreTarget.remaining <= 0) {
            finishRestoring();
            return;
        }

        if (waitForFacetwp()) {
            return;
        }

        var loadMoreButton = findLoadMoreButton();

        if (!loadMoreButton) {
            restoreTarget.attempts += 1;

            if (restoreTarget.attempts > maxRestoreAttempts) {
                finishRestoring();
                return;
            }

            queueRestoreAttempt(120);
            return;
        }

        restoreTarget.attempts = 0;
        restoreTarget.remaining -= 1;
        waitingForFacetwp = true;
        loadMoreButton.click();

        if (restoreFailSafeTimer) {
            window.clearTimeout(restoreFailSafeTimer);
        }

        restoreFailSafeTimer = window.setTimeout(function () {
            if (restoring && waitingForFacetwp) {
                waitingForFacetwp = false;
                queueRestoreAttempt(120);
            }
        }, 5000);
    };

    var startRestoring = function (state) {
        var clicks = toPositiveInteger(state.loadMoreClicks, 0);

        if (clicks < 1) {
            scrollToRestoredPosition(state);
            return;
        }

        restoring = true;
        waitingForFacetwp = false;
        restoreTarget = {
            attempts: 0,
            productId: toPositiveInteger(state.productId, 0),
            productTop: Number(state.productTop || 0),
            remaining: clicks,
            scrollY: toPositiveInteger(state.scrollY, 0)
        };

        queueRestoreAttempt(120);
    };

    var maybeRestoreOnPageShow = function (event) {
        pageShown = true;

        var state = readState();
        var shouldRestore = !!state.isReturning
            || (!!state.restoreOnReload && isReloadNavigation());

        if (!shouldRestore) {
            return;
        }

        state.isReturning = false;
        state.restoreOnReload = false;
        writeState(state);

        if (event && event.persisted) {
            scrollToRestoredPosition(state);
            return;
        }

        startRestoring(state);
    };

    var handleArchiveLinkClick = function (event) {
        var link = event.target && event.target.closest
            ? event.target.closest('a[href]')
            : null;

        if (!isSameTabNavigationLink(event, link)) {
            return;
        }

        if (isProductArchiveLink(event, link)) {
            productNavigationStarted = true;
            archiveNavigationStarted = false;
            rememberProductReturn(link);
            return;
        }

        archiveNavigationStarted = true;
        removeAllArchiveStates();
    };

    var handleLoadMoreClick = function (event) {
        var button = event.target && event.target.closest
            ? event.target.closest('.facetwp-load-more')
            : null;

        if (!button || restoring) {
            return;
        }

        rememberLoadMoreClick();
    };

    var handleFacetwpRefresh = function () {
        if (restoring) {
            return;
        }

        if (!pageShown || (window.FWP && !FWP.loaded)) {
            return;
        }

        if (pendingLoadMoreClick || (window.FWP && FWP.is_load_more)) {
            return;
        }

        removeAllArchiveStates();
    };

    var handleFacetwpLoaded = function () {
        if (restoring) {
            waitingForFacetwp = false;

            if (restoreFailSafeTimer) {
                window.clearTimeout(restoreFailSafeTimer);
                restoreFailSafeTimer = 0;
            }

            queueRestoreAttempt(80);
            return;
        }

        pendingLoadMoreClick = false;
    };

    var handlePageHide = function () {
        if (productNavigationStarted) {
            return;
        }

        if (archiveNavigationStarted) {
            removeAllArchiveStates();
            return;
        }

        rememberReloadRestore();
    };

    var handleVisibilityChange = function () {
        if ('hidden' !== document.visibilityState || productNavigationStarted) {
            return;
        }

        if (archiveNavigationStarted) {
            removeAllArchiveStates();
            return;
        }

        rememberReloadRestore();
    };

    removeLegacyState();

    document.addEventListener('click', handleArchiveLinkClick, true);
    document.addEventListener('click', handleLoadMoreClick, true);
    document.addEventListener('facetwp-refresh', handleFacetwpRefresh);
    document.addEventListener('facetwp-loaded', handleFacetwpLoaded);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', maybeRestoreOnPageShow);
})();
